import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/config/api'
import type {
  AIInsightsCombinedResponse,
  AIInsightsStatusResponse,
  AIInsightsListResponse,
} from '@/types'

const POLL_INTERVAL = 3000 // 3초마다 폴링

// Query keys
export const aiInsightsKeys = {
  all: ['ai-insights'] as const,
  latest: () => [...aiInsightsKeys.all, 'latest'] as const,
  status: () => [...aiInsightsKeys.all, 'status'] as const,
  history: (page: number, limit: number) => [...aiInsightsKeys.all, 'history', page, limit] as const,
  detail: (id: number) => [...aiInsightsKeys.all, 'detail', id] as const,
}

/**
 * Hook for fetching the latest AI insights
 */
export function useLatestAIInsights() {
  return useQuery({
    queryKey: aiInsightsKeys.latest(),
    queryFn: async (): Promise<AIInsightsCombinedResponse> => {
      const { data } = await api.get<AIInsightsCombinedResponse>('/api/ai-insights/latest')
      return data
    },
    // Poll while processing
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'received' || status === 'processing') {
        return POLL_INTERVAL
      }
      return false
    },
    retry: (failureCount, error) => {
      // Don't retry on 404
      if ((error as Error & { response?: { status: number } })?.response?.status === 404) {
        return false
      }
      return failureCount < 2
    },
  })
}

/**
 * Hook for generating AI insights
 */
export function useGenerateAIInsights() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      forceRefresh = false,
    }: {
      forceRefresh?: boolean
    } = {}): Promise<AIInsightsStatusResponse> => {
      const params: Record<string, boolean> = {}
      if (forceRefresh) params.force_refresh = true

      const { data } = await api.post<AIInsightsStatusResponse>('/api/ai-insights/generate', null, {
        params,
      })
      return data
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: aiInsightsKeys.all })
    },
  })
}

/**
 * Hook for fetching AI insights history
 */
export function useAIInsightsHistory(page = 1, limit = 30) {
  return useQuery({
    queryKey: aiInsightsKeys.history(page, limit),
    queryFn: async (): Promise<AIInsightsListResponse> => {
      const { data } = await api.get<AIInsightsListResponse>('/api/ai-insights/history', {
        params: { page, limit },
      })
      return data
    },
  })
}

/**
 * Hook for fetching a specific AI insight by ID
 */
export function useAIInsightById(id: number | null) {
  return useQuery({
    queryKey: aiInsightsKeys.detail(id ?? 0),
    queryFn: async (): Promise<AIInsightsCombinedResponse> => {
      const { data } = await api.get<AIInsightsCombinedResponse>(`/api/ai-insights/${id}`)
      return data
    },
    enabled: id !== null,
  })
}

/**
 * Combined hook for AI insights functionality
 */
export function useAIInsights() {
  const latestQuery = useLatestAIInsights()
  const generateMutation = useGenerateAIInsights()

  return {
    // Data
    data: latestQuery.data,
    result: latestQuery.data?.result ?? null,
    status: latestQuery.data?.status ?? null,
    errorMessage: latestQuery.data?.error_message ?? null,
    loading: latestQuery.isLoading,
    error: latestQuery.error ? (latestQuery.error as Error).message : null,
    refetch: latestQuery.refetch,

    // Generate action
    generate: generateMutation.mutate,
    generating: generateMutation.isPending,
    generateError: generateMutation.error ? (generateMutation.error as Error).message : null,
  }
}
